/**
 * Fleet page — operator's live view of devices enrolled in the
 * currently-selected event.
 *
 * Fleet is event-scoped via the global CurrentEventProvider. Default
 * behavior:
 *   - Read `currentEventId` from context
 *   - Fetch event enrollments + the global device-state list
 *   - Show the intersection (only devices enrolled in this event,
 *     decorated with their per-event driver name + vehicle from
 *     the enrollment row)
 *
 * Escape hatches:
 *   - `?all=1` URL flag → show the legacy global device list,
 *     unfiltered. Used for ops debugging when a device misbehaves
 *     and the operator isn't sure which event it's enrolled in
 *   - No event selected → empty state nudging the operator to pick
 *     one from the navbar pill (or use ?all=1)
 *
 * Click a device card to see DeviceDetail.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Square,
  Circle as RecordCircle,
  Search,
  ChevronRight,
  HardDrive,
  Camera,
  Smartphone,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, displayVehicle, formatBytes, formatRelative, parseUtcMs } from '@/lib/utils';
import { AUDIO_CHOICE_BADGE } from '@/lib/audioChoice';
import api from '../services/api';
import { FleetDevice, FleetEvent } from '../types';
import { EnrollmentRow, listEnrollments } from '../services/fleet';
import { useCurrentEvent } from '../state/currentEvent';
import { LG_MEDIA_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import DeviceDetail from './DeviceDetail';
import FleetDevicePanel from './FleetDevicePanel';
import SnapshotDialog from './SnapshotDialog';
import StartStagePicker, { startRecordingPayload, useStartStage } from './StartStagePicker';
import { displayCarNumber } from './viewer/carNumber';
import { LagInfo, SEVERITY_TEXT, lagFromTelemetryAge, useNowTick } from './viewer/lag';

const STATUS_BORDER: Record<string, string> = {
  online: 'border-status-online text-status-online',
  streaming: 'border-status-warning text-status-warning',
  buffered: 'border-status-buffered text-status-buffered',
  draining: 'border-status-warning text-status-warning',
  offline: 'border-fg-muted text-fg-muted',
};

const THERMAL_BORDER: Record<string, string> = {
  ok: 'border-status-online text-status-online',
  warm: 'border-status-warning text-status-warning',
  hot: 'border-status-error text-status-error',
};

/** Actively producing footage (box live/buffered, phone recording) —
 *  the design's "Live" bucket. Idle = online but not recording. */
const isLiveDevice = (d: FleetDevice): boolean =>
  d.online && d.mode != null && d.mode !== 'idle';

type FleetFilter = 'all' | 'online' | 'live' | 'idle' | 'offline';

const FILTERS: ReadonlyArray<{ key: FleetFilter; label: string; dot?: string }> = [
  { key: 'all', label: 'All' },
  { key: 'online', label: 'Online', dot: 'bg-status-online' },
  { key: 'live', label: 'Live', dot: 'bg-status-error' },
  { key: 'idle', label: 'Idle', dot: 'bg-accent-cyan' },
  { key: 'offline', label: 'Offline' },
];

const matchesFilter = (d: FleetDevice, f: FleetFilter): boolean => {
  switch (f) {
    case 'online':
      return d.online;
    case 'live':
      return isLiveDevice(d);
    case 'idle':
      return d.online && !isLiveDevice(d);
    case 'offline':
      return !d.online;
    default:
      return true;
  }
};

/** Network type shorthand for the Vehicle/device sub-line + Uplink sub-label. */
const netType = (d: FleetDevice): string | null =>
  d.network_quality?.effective_type ? d.network_quality.effective_type.toUpperCase() : null;

const TABLE_GRID =
  'grid grid-cols-[20px_48px_1.2fr_1.5fr_150px_64px_84px_84px_28px] items-center gap-2 px-3';

/** Upload-edge lag: PREFERRED source is the backend's
 *  `upload_edge_lag_s` — seconds since the newest segment of the
 *  current session arrived, i.e. how far the uploaded video trails
 *  live. Falls back to the heartbeat-age proxy when the backend
 *  hasn't seen a segment yet this process lifetime.
 *  Null for anything not actively recording. */
const lagInfoFor = (
  d: FleetDevice,
  nowMs: number,
): (LagInfo & { source: 'edge' | 'proxy' }) | null => {
  if (!isLiveDevice(d)) return null;
  if (d.upload_edge_lag_s != null) {
    // Segments land every ~6 s, so a healthy pipeline reads 0–12 s.
    const s = d.upload_edge_lag_s;
    const severity = s < 15 ? 'fresh' : s < 60 ? 'lagging' : 'stale';
    const label = s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m`;
    return { severity, label, source: 'edge' };
  }
  const ts = d.last_seen_at ? parseUtcMs(d.last_seen_at) : NaN;
  if (!Number.isFinite(ts)) return null;
  return { ...lagFromTelemetryAge(ts, nowMs), source: 'proxy' };
};

/** Row checkbox. Swallows click and key events so ticking a box never
 *  opens the device the row belongs to. */
const SelectBox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, indeterminate = false, onChange, label }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="size-4 cursor-pointer accent-[var(--accent-primary)]"
    />
  );
};

/**
 * Record / Stop for every ticked device, with one stage choice for all of
 * them. Each device gets its own command, so a phone that refuses the stage
 * fails only its own row — the reason shows in that device's command list.
 */
const FleetBulkBar: React.FC<{
  targets: FleetDevice[];
  hiddenCount: number;
  labelFor: (d: FleetDevice) => string;
  onClear: () => void;
}> = ({ targets, hiddenCount, labelFor, onClear }) => {
  const { stageOptions, startChoice, startStageLabel, setStartStage } = useStartStage();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Same per-device gates as the single Record / Stop buttons.
  const recordable = targets.filter((d) => d.mode !== 'buffered');
  const stoppable = targets.filter((d) => d.mode != null && d.mode !== 'idle');

  const send = async (
    verb: string,
    kind: string,
    payload: Record<string, number>,
    eligible: FleetDevice[],
  ) => {
    setBusy(true);
    setResult(null);
    const outcomes = await Promise.allSettled(
      eligible.map((d) => api.enqueueFleetCommand(d.device_id, kind, payload)),
    );
    const failed = eligible.filter((_, i) => outcomes[i].status === 'rejected');
    const skipped = targets.length - eligible.length;
    setResult(
      [
        `${verb} sent to ${eligible.length - failed.length}`,
        failed.length ? `failed: ${failed.map(labelFor).join(', ')}` : null,
        skipped ? `${skipped} skipped` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    );
    setBusy(false);
  };

  return (
    <div className="sticky bottom-4 z-20 mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-accent bg-bg-elevated px-3 py-2 shadow-lg">
      <span className="text-sm font-medium text-fg">{targets.length} selected</span>
      {hiddenCount > 0 && (
        <span className="text-xs text-fg-secondary">
          ({hiddenCount} hidden by filter, not included)
        </span>
      )}
      {stageOptions.length > 0 && (
        <StartStagePicker
          stages={stageOptions}
          value={startChoice}
          onChange={setStartStage}
          disabled={busy}
        />
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={busy || recordable.length === 0}
        title={
          startStageLabel
            ? `Retarget each phone to ${startStageLabel} and record`
            : "Record the stage each driver targeted on the phone"
        }
        onClick={() =>
          send(
            startStageLabel ? `Record @ ${startStageLabel}` : 'Record',
            'phone.start_recording',
            startRecordingPayload(startChoice),
            recordable,
          )
        }
        className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
      >
        <RecordCircle />
        Record{startStageLabel ? ` @ ${startStageLabel}` : ''}
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={busy || stoppable.length === 0}
        onClick={() => send('Stop', 'phone.stop_recording', {}, stoppable)}
        className="border-status-error text-status-error hover:bg-status-error/10"
      >
        <Square />
        Stop
      </Button>
      {busy && <Spinner size={16} />}
      {result && <span className="text-xs text-fg-secondary">{result}</span>}
      <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto">
        <X />
        Clear
      </Button>
    </div>
  );
};

/**
 * Rally-scale table density: one row per car. The Lag
 * column tooltip keeps the proxy honest; Uplink is actual-only for the
 * same reason (target Mbps isn't on the fleet row yet).
 */
const FleetTable: React.FC<{
  devices: FleetDevice[];
  enrollmentByDeviceId: Map<string, EnrollmentRow>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  nowMs: number;
  checkedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}> = ({
  devices,
  enrollmentByDeviceId,
  selectedId,
  onSelect,
  nowMs,
  checkedIds,
  onToggle,
  onToggleAll,
}) => {
  const checkedCount = devices.filter((d) => checkedIds.has(d.device_id)).length;
  return (
    <Card className="overflow-hidden border-border bg-bg-surface">
      <CardContent className="p-0">
        <div
          className={cn(
            TABLE_GRID,
            'border-b border-border bg-bg-elevated py-2 text-[10px] font-semibold uppercase tracking-wider text-fg-secondary',
          )}
        >
          <SelectBox
            label="Select all shown devices"
            checked={checkedCount > 0 && checkedCount === devices.length}
            indeterminate={checkedCount > 0 && checkedCount < devices.length}
            onChange={onToggleAll}
          />
          <span>#</span>
          <span>Crew</span>
          <span>Vehicle / device</span>
          <span>Status</span>
          {/* Real metrics. Lag = how far the uploaded video trails
              live (backend segment-arrival stamp); Uplink = measured
              average upload throughput. */}
          <span title="How far the uploaded video trails live (time since the newest segment arrived). Falls back to heartbeat freshness right after a backend restart.">
            Lag
          </span>
          <span title="Measured average upload throughput — last 10 minutes while active, else the last session's average.">
            Uplink
          </span>
          <span>Last seen</span>
          <span />
        </div>
        {devices.map((d) => {
          const enrollment = enrollmentByDeviceId.get(d.device_id);
          const statusClass = STATUS_BORDER[d.status] || STATUS_BORDER.offline;
          const lag = lagInfoFor(d, nowMs);
          // Measured upload average; the network-API downlink
          // ESTIMATE only survives as tooltip context.
          const uplink = d.upload_avg_mbps ?? null;
          const netEstimate = d.network_quality?.downlink_mbps ?? null;
          const net = netType(d);
          const seen = formatRelative(d.last_seen_at);
          return (
            <div
              key={d.device_id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(d.device_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(d.device_id);
                }
              }}
              className={cn(
                TABLE_GRID,
                'cursor-pointer border-b border-border py-2 transition-colors last:border-b-0 hover:bg-bg-elevated',
                d.device_id === selectedId &&
                  'bg-bg-elevated shadow-[inset_2px_0_0_var(--accent-primary)]',
              )}
            >
              <SelectBox
                label={`Select ${enrollment?.driver_name || d.name || d.device_id}`}
                checked={checkedIds.has(d.device_id)}
                onChange={() => onToggle(d.device_id)}
              />
              <span className="font-mono text-sm font-bold text-accent">
                {enrollment
                  ? displayCarNumber({ car_number: enrollment.car_number, enrollment_id: enrollment.id })
                  : '—'}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Smartphone className="size-3 shrink-0 text-accent-purple" />
                  <span className="truncate text-sm font-medium text-fg">
                    {enrollment?.driver_name || d.name || d.device_id}
                  </span>
                </div>
                <div className="truncate font-mono text-[10px] text-fg-muted">
                  {d.hostname || d.kind || 'device'}
                </div>
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm text-fg">
                  {displayVehicle(enrollment?.vehicle) || '—'}
                </div>
                <div className="truncate font-mono text-[10px] text-fg-muted">
                  {d.device_id}
                  {net ? ` · ${net}` : ''}
                </div>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                <Badge variant="outline" className={cn('font-mono', statusClass)}>
                  {d.status}
                </Badge>
                {d.capture_override?.tiers != null && (
                  <Badge
                    variant="outline"
                    title="Quality override active — this device runs non-global encoder settings (DeviceDetail → Quality override)"
                    className="whitespace-nowrap border-accent-purple/60 font-mono text-accent-purple"
                  >
                    Q≠
                  </Badge>
                )}
                {enrollment && (
                  // Same last-known semantics as the cards: an armed
                  // phone that drops offline reads "armed · stale".
                  <Badge
                    variant="outline"
                    title={
                      !d.online && enrollment.state === 'armed'
                        ? 'Last known state — device offline; commands will queue until it reconnects'
                        : undefined
                    }
                    className={cn(
                      'whitespace-nowrap font-mono',
                      enrollment.state === 'armed'
                        ? d.online
                          ? 'border-status-online text-status-online'
                          : 'border-status-warning/60 text-status-warning'
                        : 'border-fg-muted text-fg-muted',
                    )}
                  >
                    {enrollment.state}
                    {!d.online && enrollment.state === 'armed' && ' · stale'}
                  </Badge>
                )}
              </div>
              <span
                className={cn(
                  'font-mono text-xs',
                  lag ? SEVERITY_TEXT[lag.severity] : 'text-fg-muted',
                )}
                title={
                  lag
                    ? lag.source === 'edge'
                      ? 'Uploaded video trails live by this much (newest segment arrival)'
                      : 'Telemetry freshness (proxy) — no segment seen since backend restart'
                    : 'Not recording'
                }
              >
                {lag ? lag.label : '—'}
              </span>
              <span
                className={cn('font-mono text-xs', uplink != null ? 'text-fg' : 'text-fg-muted')}
                title={[
                  uplink != null
                    ? `Measured upload average${netEstimate != null ? ` · phone's network downlink estimate ${netEstimate.toFixed(1)} Mb/s` : ''}`
                    : 'No measured upload yet',
                  // The on-demand measurement, when one exists.
                  d.last_speed_test != null
                    ? `last speed test: ${d.last_speed_test.mbps.toFixed(1)} Mb/s · ${new Date(d.last_speed_test.created_at).toLocaleDateString()}`
                    : null,
                ]
                  .filter(Boolean)
                  .join('\n')}
              >
                {uplink != null ? (
                  <>
                    {uplink.toFixed(1)}
                    <span className="text-[10px] text-fg-muted"> Mb/s</span>
                  </>
                ) : (
                  '—'
                )}
              </span>
              <span
                className={cn(
                  'font-mono text-[11px]',
                  d.online ? 'text-fg-secondary' : 'text-status-error',
                )}
              >
                {seen}
              </span>
              <ChevronRight className="size-4 text-fg-muted" />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

interface FleetProps {
  selectedId?: string | null;
  onSelect: (id: string | null) => void;
}

export const Fleet: React.FC<FleetProps> = ({ selectedId, onSelect }) => {
  const { currentEventId, currentEvent } = useCurrentEvent();
  // ?all=1 toggles the legacy global view (debug / ops use). Read on
  // mount only; no live URL listener needed since this is a one-shot
  // escape hatch.
  const showAllDevices = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('all') === '1';
  }, []);

  const [devices, setDevices] = useState<FleetDevice[] | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<{
    deviceId: string;
    cameraId: string;
    label: string;
  } | null>(null);
  // Search + status chips — pattern lifted from RosterTab.
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FleetFilter>('all');
  // Ticked devices for the bulk bar. Device ids are only meaningful within
  // one event's fleet, so switching event starts from an empty selection.
  const [checkedIds, setCheckedIds] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => setCheckedIds(new Set()), [currentEventId]);
  // Desktop: row click opens the SIDE PANEL so the fleet overview stays
  // visible mid-incident; `expanded` is the full-page escape.
  // Below lg the cards keep their full-page DeviceDetail behavior.
  const isDesktop = useMediaQuery(LG_MEDIA_QUERY);
  const [expanded, setExpanded] = useState(false);
  // A new (or cleared) selection always lands in the panel first —
  // without this, expanded leaks across selections (e.g. expand on
  // desktop, back on a narrow window, reselect on desktop).
  useEffect(() => {
    setExpanded(false);
  }, [selectedId]);
  // 1 Hz tick ages the Lag + Last-seen columns between polls.
  const nowMs = useNowTick(1000);

  const refresh = useCallback(async () => {
    try {
      // Event-scoped by default so an ORGANIZER (not just a superadmin)
      // can see their event's fleet: the global `/fleet/devices` list is
      // superadmin-only, so reserve it for the ?all=1 escape hatch. With
      // no event selected there's nothing event-scoped to fetch — the
      // empty-state UI (visibleDevices) covers that.
      if (showAllDevices) {
        setDevices(await api.listFleetDevices());
      } else if (currentEventId != null) {
        setDevices(await api.listEventFleetDevices(currentEventId));
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.warn('device list refresh failed', err);
      setDevices([]);
    }
  }, [showAllDevices, currentEventId]);

  // Event-scoped enrollment fetch. Skipped entirely when ?all=1 (legacy
  // global view) OR when no event is selected (the empty-state UI
  // handles that case below).
  useEffect(() => {
    if (showAllDevices || currentEventId == null) {
      setEnrollments([]);
      return;
    }
    let cancelled = false;
    const refreshEnrollments = async () => {
      try {
        const rows = await listEnrollments(currentEventId);
        if (!cancelled) setEnrollments(rows);
      } catch (err) {
        if (!cancelled) {
          console.warn('listEnrollments failed', err);
          setEnrollments([]);
        }
      }
    };
    void refreshEnrollments();
    const id = window.setInterval(refreshEnrollments, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [currentEventId, showAllDevices]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 8000);
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type === 'device_online' || evt.type === 'device_offline') {
        refresh();
      }
      if (evt.type === 'command_update') {
        if (evt.command.status === 'done' || evt.command.status === 'failed') {
          refresh();
        }
      }
    });
    return () => {
      clearInterval(id);
      unsub();
    };
  }, [refresh]);

  const sendCommand = async (
    deviceId: string,
    kind: string,
    payload: Record<string, any> = {},
  ) => {
    setBusyId(deviceId);
    try {
      await api.enqueueFleetCommand(deviceId, kind, payload);
    } catch (err) {
      console.warn('enqueueFleetCommand failed', err);
    } finally {
      setBusyId(null);
    }
  };

  // Event-scoped enrollment lookup. Pending + rejected rows are
  // filtered out — pending lives on the Roster tab; rejected is
  // terminal. Armed + disarmed both surface in the Fleet view.
  const enrollmentByDeviceId = useMemo(() => {
    const m = new Map<string, EnrollmentRow>();
    for (const e of enrollments) {
      if (e.state === 'armed' || e.state === 'disarmed') {
        m.set(e.device_id, e);
      }
    }
    return m;
  }, [enrollments]);

  // The display set. When showAllDevices the global list passes
  // through; otherwise filter to devices with an active enrollment
  // in the currently-selected event.
  const visibleDevices = useMemo(() => {
    if (devices == null) return null;
    if (showAllDevices) return devices;
    if (currentEventId == null) return [];
    return devices.filter((d) => enrollmentByDeviceId.has(d.device_id));
  }, [devices, showAllDevices, currentEventId, enrollmentByDeviceId]);

  const selected = useMemo(
    () =>
      visibleDevices && selectedId
        ? visibleDevices.find((d) => d.device_id === selectedId) || null
        : null,
    [visibleDevices, selectedId],
  );

  // Status-chip counts come from the event-scoped set, independent of
  // the active search/filter (mirrors the design's filter row).
  const counts = useMemo(() => {
    const list = visibleDevices ?? [];
    return {
      all: list.length,
      online: list.filter((d) => d.online).length,
      live: list.filter(isLiveDevice).length,
      idle: list.filter((d) => d.online && !isLiveDevice(d)).length,
      offline: list.filter((d) => !d.online).length,
    };
  }, [visibleDevices]);

  // Search by car number, driver, vehicle, device id/hostname,
  // then the status chip; live rows first, then by car number, so the
  // table scans like the rally entry list.
  const filteredDevices = useMemo(() => {
    if (visibleDevices == null) return [];
    const term = search.trim().toLowerCase();
    const matchesText = (d: FleetDevice): boolean => {
      if (!term) return true;
      const e = enrollmentByDeviceId.get(d.device_id);
      return [
        e ? String(e.id) : null,
        e?.driver_name,
        e?.vehicle,
        d.device_id,
        d.hostname,
        d.name,
      ].some((v) => v != null && v.toLowerCase().includes(term));
    };
    const rank = (d: FleetDevice) => (isLiveDevice(d) ? 0 : d.online ? 1 : 2);
    return visibleDevices
      .filter((d) => matchesText(d) && matchesFilter(d, filter))
      .sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r !== 0) return r;
        const ea = enrollmentByDeviceId.get(a.device_id)?.id ?? Number.MAX_SAFE_INTEGER;
        const eb = enrollmentByDeviceId.get(b.device_id)?.id ?? Number.MAX_SAFE_INTEGER;
        return ea - eb || a.device_id.localeCompare(b.device_id);
      });
  }, [visibleDevices, search, filter, enrollmentByDeviceId]);

  // The bulk bar only acts on devices the operator can see: a ticked device
  // the search or status chip hides is counted but left out.
  const bulkTargets = useMemo(
    () => filteredDevices.filter((d) => checkedIds.has(d.device_id)),
    [filteredDevices, checkedIds],
  );
  const hiddenCheckedCount = useMemo(
    () => (visibleDevices ?? []).filter((d) => checkedIds.has(d.device_id)).length - bulkTargets.length,
    [visibleDevices, checkedIds, bulkTargets],
  );
  const toggleChecked = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllShown = () =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      const allShown = filteredDevices.every((d) => prev.has(d.device_id));
      for (const d of filteredDevices) {
        if (allShown) next.delete(d.device_id);
        else next.add(d.device_id);
      }
      return next;
    });
  const labelFor = (d: FleetDevice): string => {
    const e = enrollmentByDeviceId.get(d.device_id);
    return e
      ? `#${displayCarNumber({ car_number: e.car_number, enrollment_id: e.id })}`
      : d.name || d.device_id;
  };

  // Full-page detail: always below lg (cards keep their old behavior);
  // on lg only via the panel's expand button.
  if (selected && (!isDesktop || expanded)) {
    return (
      <DeviceDetail
        device={selected}
        onBack={() => {
          if (isDesktop) setExpanded(false);
          else onSelect(null);
        }}
        onCommand={(kind, payload) => sendCommand(selected.device_id, kind, payload)}
        onRefresh={refresh}
      />
    );
  }

  if (devices === null || visibleDevices === null) {
    return (
      <div className="mt-12 flex justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  // Empty-state branches per the routing decisions above.
  if (!showAllDevices && currentEventId == null) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="mb-2 text-2xl font-semibold text-accent">Fleet</h1>
        <p className="text-fg-secondary">
          Pick an event from the navbar pill to see its enrolled vehicles.
        </p>
        <p className="mt-2 text-xs text-fg-secondary">
          Ops view: open{' '}
          <a href="?all=1" className="underline hover:text-fg">
            ?all=1
          </a>{' '}
          to see every paired device regardless of event.
        </p>
      </div>
    );
  }

  if (visibleDevices.length === 0) {
    if (showAllDevices) {
      return (
        <div className="mx-auto max-w-6xl px-4 py-8">
          <p className="text-fg-secondary">
            No devices paired. Enroll a phone from the Event tab&apos;s QR code
            (or a join code) and it will appear here within seconds.
          </p>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="mb-2 text-2xl font-semibold text-accent">Fleet</h1>
        <p className="text-fg-secondary">
          No vehicles enrolled in{' '}
          <span className="font-mono">{currentEvent?.name ?? 'this event'}</span> yet.
        </p>
        <p className="mt-2 text-xs text-fg-secondary">
          Share the join code on the Event tab so drivers can sign up — approved
          enrollments appear here. Or visit the{' '}
          <a href="/roster" className="text-accent underline hover:text-accent">
            Roster tab
          </a>{' '}
          to approve pending sign-ups.
        </p>
      </div>
    );
  }

  const onlineCount = counts.online;
  const totalCount = counts.all;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-4 flex items-center gap-2">
        <h1 className="flex-1 text-2xl font-semibold text-accent">
          Fleet
          {!showAllDevices && currentEvent && (
            <span className="ml-2 text-sm font-normal text-fg-secondary">
              · {currentEvent.name}
            </span>
          )}
          {showAllDevices && (
            <span className="ml-2 text-sm font-normal text-fg-secondary">
              · all devices
            </span>
          )}
        </h1>
        <span className="text-sm text-fg-secondary">
          {onlineCount} online · {totalCount} total
        </span>
      </div>

      {/* Search + status chips (RosterTab pattern) */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-fg-secondary" />
          <Input
            placeholder="Search car number, driver, vehicle, device…"
            aria-label="Search devices by car number, driver, vehicle or device id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filter === f.key
                // text-fg-inverse, NOT text-bg-base: the palette maps
                // bg.DEFAULT → --bg-base, so 'bg-base' classes are never
                // generated by Tailwind — they no-op silently.
                ? 'border-accent bg-accent text-fg-inverse'
                : 'border-border bg-bg-surface text-fg-secondary hover:bg-bg-elevated',
            )}
          >
            {f.dot && (
              <span
                className={cn(
                  'inline-block h-1.5 w-1.5 rounded-full',
                  filter === f.key ? 'bg-fg-inverse/70' : f.dot,
                )}
              />
            )}
            {f.label}
            <span
              className={cn(
                'font-mono text-[10px]',
                filter === f.key ? 'text-fg-inverse/70' : 'text-fg-secondary',
              )}
            >
              {counts[f.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          {filteredDevices.length === 0 ? (
            <Card className="border-border bg-bg-surface">
              <CardContent className="px-4 py-8 text-center text-sm text-fg-secondary">
                No devices match.
              </CardContent>
            </Card>
          ) : isDesktop ? (
            <FleetTable
              devices={filteredDevices}
              enrollmentByDeviceId={enrollmentByDeviceId}
              selectedId={selectedId ?? null}
              onSelect={(id) => onSelect(id)}
              nowMs={nowMs}
              checkedIds={checkedIds}
              onToggle={toggleChecked}
              onToggleAll={toggleAllShown}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {filteredDevices.map((d) => {
                const statusClass = STATUS_BORDER[d.status] || STATUS_BORDER.offline;
                const enrollment = enrollmentByDeviceId.get(d.device_id);
                return (
                  <Card
                    key={d.device_id}
                    onClick={() => onSelect(d.device_id)}
                    className={cn(
                      'cursor-pointer border bg-bg-surface transition-colors duration-fast',
                      d.online ? 'border-status-online/30' : 'border-border',
                      'hover:border-accent',
                    )}
                  >
                    <CardContent className="p-4">
                      {/* flex-wrap + nowrap badges: on phones the long
                          "last 6h ago" badge used to wrap INSIDE itself
                          into a broken-looking circle. */}
                      <div className="mb-2 flex flex-wrap items-center gap-2 [&>.inline-flex]:whitespace-nowrap">
                        <SelectBox
                          label={`Select ${enrollment?.driver_name || d.name || d.device_id}`}
                          checked={checkedIds.has(d.device_id)}
                          onChange={() => toggleChecked(d.device_id)}
                        />
                        <Smartphone className="size-4 text-accent-purple" />
                        <span className="font-mono text-base">
                          {enrollment?.driver_name || d.name || d.device_id}
                        </span>
                        <Badge variant="outline" className={cn('font-mono', statusClass)}>
                          {d.status}
                        </Badge>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'font-mono',
                                  d.online
                                    ? 'border-status-online/50 text-fg-secondary'
                                    : 'border-fg-muted text-fg-muted',
                                )}
                              >
                                {d.online ? 'online' : 'last'} {formatRelative(d.last_seen_at)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {d.online
                                ? `Connected · last heartbeat ${d.last_ws_at ?? d.last_seen_at ?? '—'}`
                                : `Offline · last seen ${d.last_seen_at ?? 'never'}`}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {enrollment && (
                          // Offline devices keep their LAST KNOWN arm
                          // state (commands queue by design) — an armed
                          // phone that drops offline shows "armed ·
                          // stale", not a bare "offline".
                          <Badge
                            variant="outline"
                            title={
                              !d.online && enrollment.state === 'armed'
                                ? 'Last known state — device offline; commands will queue until it reconnects'
                                : undefined
                            }
                            className={cn(
                              'font-mono',
                              enrollment.state === 'armed'
                                ? d.online
                                  ? 'border-status-online text-status-online'
                                  : 'border-status-warning/60 text-status-warning'
                                : 'border-fg-muted text-fg-muted',
                            )}
                          >
                            {enrollment.state}
                            {!d.online && enrollment.state === 'armed' && ' · stale'}
                          </Badge>
                        )}
                        <div className="flex-1" />
                        <ChevronRight className="size-4 text-fg-muted" />
                      </div>

                      <p className="text-sm text-fg-secondary">
                        {displayVehicle(enrollment?.vehicle) ? (
                          <>
                            <span className="text-fg">{displayVehicle(enrollment?.vehicle)}</span>
                            <span> · </span>
                          </>
                        ) : null}
                        {d.hostname || d.device_id} · v{d.version || '—'} · mode{' '}
                        {d.mode || 'idle'}
                      </p>

                      {d.kind === 'phone' && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {d.battery?.level != null && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'font-mono',
                                d.battery.level >= 0.5
                                  ? 'border-status-online text-status-online'
                                  : d.battery.level >= 0.2
                                    ? 'border-status-warning text-status-warning'
                                    : 'border-status-error text-status-error',
                              )}
                            >
                              {Math.round(d.battery.level * 100)}%{d.battery.charging ? ' ⚡' : ''}
                            </Badge>
                          )}
                          {d.gps?.lat != null && d.gps?.lon != null && (
                            // Raw coordinates are only useful as a jump
                            // — open the position in a map tab (the
                            // in-app fleet map was retired).
                            <a
                              href={`https://www.google.com/maps?q=${d.gps.lat},${d.gps.lon}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Open last GPS fix in Google Maps"
                            >
                              <Badge
                                variant="outline"
                                className="border-status-buffered font-mono text-status-buffered hover:bg-status-buffered/10"
                              >
                                {d.gps.lat.toFixed(4)}, {d.gps.lon.toFixed(4)} ↗
                              </Badge>
                            </a>
                          )}
                          {d.network_quality?.effective_type && (
                            <Badge
                              variant="outline"
                              className="font-mono text-fg"
                              title="Phone's own network downlink estimate — not measured upload throughput"
                            >
                              {d.network_quality.effective_type}{' '}
                              {d.network_quality.downlink_mbps?.toFixed?.(1) ?? '—'}Mb
                            </Badge>
                          )}
                          <TooltipProvider>
                            {d.thermal_state && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'font-mono',
                                      THERMAL_BORDER[d.thermal_state] || 'border-fg-muted text-fg-muted',
                                    )}
                                  >
                                    th: {d.thermal_state}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Phone thermal state</TooltipContent>
                              </Tooltip>
                            )}
                            {d.fgs_running != null && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'font-mono',
                                      d.fgs_running
                                        ? 'border-status-online text-status-online'
                                        : 'border-fg-muted text-fg-muted',
                                    )}
                                  >
                                    {d.fgs_running ? 'fgs ✓' : 'fgs ✗'}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Foreground service running (Android)</TooltipContent>
                              </Tooltip>
                            )}
                            {d.audio_choice === 'off' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={cn('font-mono', AUDIO_CHOICE_BADGE.off.className)}
                                  >
                                    audio off
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>{AUDIO_CHOICE_BADGE.off.title}</TooltipContent>
                              </Tooltip>
                            )}
                            {(d.upload_backlog_stream_mb != null ||
                              d.upload_backlog_archive_mb != null) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'font-mono text-fg',
                                      (d.upload_backlog_stream_mb ?? 0) +
                                        (d.upload_backlog_archive_mb ?? 0) >
                                        100
                                        ? 'border-status-warning'
                                        : 'border-border',
                                    )}
                                  >
                                    S {(d.upload_backlog_stream_mb ?? 0).toFixed(1)} / A{' '}
                                    {(d.upload_backlog_archive_mb ?? 0).toFixed(1)} MB
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Upload backlog by tier (low quality / high quality)</TooltipContent>
                              </Tooltip>
                            )}
                            {d.active_rally_event && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className="border-accent-purple font-mono text-accent-purple"
                                  >
                                    ev: {d.active_rally_event}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Active rally event</TooltipContent>
                              </Tooltip>
                            )}
                          </TooltipProvider>
                        </div>
                      )}

                      <div className="mt-2 flex items-center gap-2">
                        <HardDrive className="size-4 text-fg-muted" />
                        <span className="text-xs text-fg-secondary">
                          disk {formatBytes(d.disk_free_bytes)} free / {formatBytes(d.disk_total_bytes)}
                        </span>
                        {d.upload_pending != null && d.upload_pending > 0 && (
                          <Badge
                            variant="outline"
                            className="border-status-warning font-mono text-status-warning"
                          >
                            {d.upload_pending} pending
                          </Badge>
                        )}
                      </div>

                      <div
                        className="mt-2 flex flex-wrap gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TooltipProvider>
                          {d.cameras.map((c) => {
                            const noSignal = c.is_hdmi && c.signal_locked === false;
                            const tooltip =
                              `${c.camera_id} · ${c.type || ''} ${c.resolution || ''}` +
                              (c.is_hdmi
                                ? c.signal_locked === true
                                  ? ' · HDMI signal locked'
                                  : c.signal_locked === false
                                    ? ' · HDMI no signal'
                                    : ' · HDMI'
                                : '');
                            return (
                              <Tooltip key={c.camera_id}>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() =>
                                      setPreviewTarget({
                                        deviceId: d.device_id,
                                        cameraId: c.name || c.camera_id,
                                        label: c.name || c.camera_id,
                                      })
                                    }
                                    className={cn(
                                      'inline-flex items-center gap-1 rounded-full border bg-bg-elevated px-2.5 py-0.5 font-mono text-xs',
                                      noSignal
                                        ? 'border-status-warning text-status-warning'
                                        : c.streaming_enabled
                                          ? 'border-border text-fg'
                                          : 'border-border text-fg-muted',
                                    )}
                                  >
                                    <Camera className="size-3" />
                                    {c.name || c.camera_id}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{tooltip}</TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </TooltipProvider>
                        {d.cameras.length === 0 && (
                          <span className="text-xs text-fg-muted">no cameras</span>
                        )}
                      </div>

                      <div
                        className="mt-3 flex flex-wrap gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TooltipProvider>
                          {/* Record/Stop intentionally stay enabled while
                              the phone is offline — backend command queue
                              persists `queued` rows and drains them on the
                              next WS connect. The "last seen" badge above
                              and the queued-command list in DeviceDetail
                              give the operator the offline signal. */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={
                                    busyId === d.device_id ||
                                    d.mode === 'buffered'
                                  }
                                  onClick={() =>
                                    sendCommand(d.device_id, 'phone.start_recording', {})
                                  }
                                  className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
                                >
                                  <RecordCircle />
                                  Record
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {d.online
                                ? 'Tell the phone to start recording'
                                : 'Phone offline — command will queue and run on reconnect'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={
                                    busyId === d.device_id ||
                                    d.mode === 'idle' ||
                                    d.mode == null
                                  }
                                  onClick={() =>
                                    sendCommand(d.device_id, 'phone.stop_recording', {})
                                  }
                                  className="border-status-error text-status-error hover:bg-status-error/10"
                                >
                                  <Square />
                                  Stop
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {d.online
                                ? 'Stop phone recording'
                                : 'Phone offline — stop will queue and run on reconnect'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Side detail panel (mockup .detail-panel):
            the list stays visible; the expand button escapes to the
            full DeviceDetail page. */}
        {isDesktop && selected && !expanded && (
          <div className="sticky top-4 max-h-[calc(100dvh-2rem)] w-[400px] shrink-0 overflow-y-auto rounded-lg border border-border bg-bg-surface">
            <FleetDevicePanel
              device={selected}
              enrollment={enrollmentByDeviceId.get(selected.device_id) ?? null}
              lag={lagInfoFor(selected, nowMs)}
              busy={busyId === selected.device_id}
              eventId={showAllDevices ? null : currentEventId}
              onClose={() => onSelect(null)}
              onExpand={() => setExpanded(true)}
              onCommand={(kind, payload) => sendCommand(selected.device_id, kind, payload)}
            />
          </div>
        )}
      </div>

      {bulkTargets.length + hiddenCheckedCount > 0 && (
        <FleetBulkBar
          targets={bulkTargets}
          hiddenCount={hiddenCheckedCount}
          labelFor={labelFor}
          onClear={() => setCheckedIds(new Set())}
        />
      )}

      {previewTarget && (
        <SnapshotDialog
          open={!!previewTarget}
          onClose={() => setPreviewTarget(null)}
          deviceId={previewTarget.deviceId}
          cameraId={previewTarget.cameraId}
          cameraLabel={previewTarget.label}
        />
      )}
    </div>
  );
};

export default Fleet;
